/**
 * Civil time for the sun (Sprint 084.0, ADR-0091).
 *
 * The sun answers to an **instant**; a person names a **local date and time**
 * in the place the project is. This module is the only road between the two.
 *
 * ## Two kinds of zone, and why only one uses `Intl`
 *
 * A named IANA zone carries its own summer-time rules, and `Intl` is the only
 * source of them this package trusts. A **fixed offset** — the approximate zone
 * a site gets when no country names one (DEC-5) — is plain arithmetic. `Intl`'s
 * own offset zones (`'+00:30'`) are recent: CI runs Node 20 and a published
 * package runs in runtimes nobody here chooses, so they are never asked for.
 *
 * ## The two hours a year that are not one-to-one (DEC-4)
 *
 * - **The spring gap** — 02:30 on the morning clocks go forward does not
 *   exist. It resolves **forward**: read with the offset in force before the
 *   change, which lands on the same instant a clock reading 03:30 names.
 * - **The autumn overlap** — 02:30 happens twice. It resolves to the **first**.
 *
 * Nothing here reads a clock. A caller that needs "now" passes it.
 */
const MINUTE = 60_000;
const DAY_MINUTES = 1440;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_PATTERN = /^(\d{2}):(\d{2})$/;
export function parseLocalDate(text) {
    if (typeof text !== 'string') {
        return { ok: false, reason: 'A date is written YYYY-MM-DD.' };
    }
    const match = DATE_PATTERN.exec(text);
    if (match === null) {
        return { ok: false, reason: 'A date is written YYYY-MM-DD.' };
    }
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (probe.getUTCFullYear() !== year ||
        probe.getUTCMonth() !== month - 1 ||
        probe.getUTCDate() !== day) {
        return { ok: false, reason: `${text} is not a day of the calendar.` };
    }
    return { ok: true, value: text };
}
export function parseLocalTime(text) {
    if (typeof text !== 'string') {
        return { ok: false, reason: 'A time is written HH:MM, from 00:00 to 23:59.' };
    }
    const match = TIME_PATTERN.exec(text);
    if (match === null || Number(match[1]) > 23 || Number(match[2]) > 59) {
        return { ok: false, reason: 'A time is written HH:MM, from 00:00 to 23:59.' };
    }
    return { ok: true, value: text };
}
/** The zone's display name: the IANA name, or `UTC+00:30` for an offset. */
export function zoneName(zone) {
    if (zone.kind === 'iana') {
        return zone.name;
    }
    const sign = zone.minutes < 0 ? '-' : '+';
    const magnitude = Math.abs(zone.minutes);
    return `UTC${sign}${pad(Math.floor(magnitude / 60))}:${pad(magnitude % 60)}`;
}
/** Whether this runtime can read an IANA zone. A profile may name one it cannot. */
export function isKnownZone(name) {
    try {
        formatterFor(name);
        return true;
    }
    catch {
        return false;
    }
}
/** Minutes the zone is ahead of UTC at an instant. */
export function offsetAt(zone, instant) {
    if (zone.kind === 'offset') {
        return zone.minutes;
    }
    const wall = wallClock(zone.name, instant);
    return Math.round((wall - Math.floor(instant / MINUTE) * MINUTE) / MINUTE);
}
/** The local date and time an instant reads as, to the minute (floored). */
export function instantToLocal(instant, zone) {
    const wall = Math.floor(instant / MINUTE) * MINUTE + offsetAt(zone, instant) * MINUTE;
    const date = new Date(wall);
    return {
        localDate: `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`,
        localTime: `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`
    };
}
/**
 * The instant a local date and time names, with the gap resolved forward and
 * the overlap to its first occurrence. The inputs must already be parsed.
 */
export function localToInstant(localDate, localTime, zone) {
    const wall = wallMillis(localDate, localTime);
    if (zone.kind === 'offset') {
        return wall - zone.minutes * MINUTE;
    }
    // The offsets in force a day either side bound any transition that day.
    const before = offsetAt(zone, wall - DAY_MINUTES * MINUTE);
    const after = offsetAt(zone, wall + DAY_MINUTES * MINUTE);
    const candidates = [...new Set([before, after])]
        .map((offset) => wall - offset * MINUTE)
        .filter((instant) => wall - offsetAt(zone, instant) * MINUTE === instant)
        .sort((a, b) => a - b);
    // No candidate reads back as the wall time: the gap. Read it with the offset
    // in force before the change, which lands after it (DEC-4, forward).
    return candidates[0] ?? wall - before * MINUTE;
}
/** Minutes past local midnight, for a `HH:MM`. */
export function minutesOf(localTime) {
    const [hours, minutes] = localTime.split(':').map(Number);
    return (hours ?? 0) * 60 + (minutes ?? 0);
}
/** The next calendar date after `localDate`. */
export function nextDate(localDate) {
    const next = new Date(wallMillis(localDate, '00:00') + DAY_MINUTES * MINUTE);
    return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}
/** The wall clock as if it were UTC — the arithmetic every conversion uses. */
function wallMillis(localDate, localTime) {
    const [year, month, day] = localDate.split('-').map(Number);
    return Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1) + minutesOf(localTime) * MINUTE;
}
function wallClock(name, instant) {
    const parts = formatterFor(name).formatToParts(instant);
    const value = (type) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    return Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'));
}
const formatters = new Map();
function formatterFor(name) {
    let formatter = formatters.get(name);
    if (formatter === undefined) {
        formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: name,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
        formatters.set(name, formatter);
    }
    return formatter;
}
function pad(value) {
    return String(value).padStart(2, '0');
}
//# sourceMappingURL=time.js.map